import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const bulkEmailGuide: AdminGuide = {
  id: "bulk-email",
  title:
    "Send Email & Comms broadcasts safely (templates, targeting, and activity)",
  category: "Customer Support",
  audience: ["Ops", "CS"],
  summary:
    "Queue an Email & Comms broadcast to a targeted audience, with a test-email step and a “don’t email the wrong people” checklist.",
  keywords: [
    "bulk email",
    "send-bulk-email",
    "templates",
    "re-engagement",
    "never logged in",
    "inactive",
    "job id",
    "recipients",
  ],
  location: "Admin",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.BULK_EMAIL,
    GUIDE_SOURCE_DOCS.ADMIN_REQUIREMENTS,
    GUIDE_SOURCE_DOCS.ADMIN_TESTING,
    GUIDE_SOURCE_DOCS.ADMIN_API_DOCS,
  ],
  warnings: [
    "Medium risk: bulk email is hard to undo. Stop if you are unsure about the target segment or the email content.",
    "Do not include credential-like links in bulk emails (invite links `inviteCode=...`, spoof links `token=...`, or any auth tokens).",
    "Always send a test email to yourself before sending to a segment.",
  ],
  safetyNotes: [
    "Broadcasts are queued and processed in the background. Keep the job ID for tracking and escalation.",
    "Only log/share counts and job IDs internally. Do not paste recipient lists into tickets.",
  ],
  whatThisHelpsYouDo: [
    "Choose a safe target segment (active/inactive/never logged in, etc.).",
    "Validate formatting by sending a test email to yourself first.",
    "Queue the broadcast job and track it using the job ID.",
  ],
  whenToUse: [
    "Ops/CS has approved copy and needs to send a campaign to a known segment.",
    "You need to send a reconciliation reminder or re-engagement email using a standard template.",
  ],
  whenNotToUse: [
    "You have not approved the email copy or you are improvising the message.",
    "You need to email a custom hand-picked list (use direct outreach or an approved workflow).",
    "The email would contain sensitive links or customer-specific data.",
  ],
  beforeYouStart: [
    "Approved email subject and message body (or an approved template).",
    "A test email address you control.",
    "Clarity on the target segment (all/active/inactive/never logged in/cancelled).",
    "A plan to monitor outcomes and respond to replies.",
  ],
  steps: [
    "Open Admin -> **Email & Comms** (`/email-comms`).",
    "Pick a template (optional) and review the subject + message.",
    "Queue a **Test send** to yourself and verify formatting in your inbox (links, line breaks, mobile readability).",
    "Select the target audience segment (for example: Never Logged In, Inactive Users).",
    "Click **Load recipients** and review the recipient count. Spot-check a few recipients if needed.",
    "Click **Queue to N recipients** to queue the job.",
    "Copy the job ID from the success message.",
    "Open **Email & Comms activity** (`/email-comms/activity`) to monitor job progress and failures.",
  ],
  checklists: [
    {
      title: "Don’t email the wrong people checklist",
      items: [
        "I selected the correct segment and verified the recipient count.",
        "I sent a test email to myself and checked formatting.",
        "The email does not contain credential-like links (`inviteCode=...`, `token=...`).",
        "The message includes only approved links (product site/help pages) and no customer-specific data.",
        "I recorded the job ID in the ticket/case.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "A bulk email job is queued successfully and you have the job ID.",
    "The job completes without unexpected failures or complaints.",
    "The message reaches the intended segment only.",
  ],
  troubleshooting: [
    {
      title: "Load recipients returns zero",
      body: [
        "Confirm the selected segment (some segments can legitimately be empty).",
        "Try “All Users” to confirm the page can load data at all.",
        "If it still fails, escalate with the segment name and the time you attempted.",
      ],
    },
    {
      title: "Broadcast queues but recipients say they never received it",
      body: [
        "Check Email & Comms activity for failures/bounces and capture the job ID.",
        "Confirm the recipient emails exist and are valid (some users may not have emails).",
        "Escalate with the job ID and approximate time the job was queued.",
      ],
    },
  ],
  escalateWhen: [
    "You think you selected the wrong segment (stop immediately; include the job ID).",
    "A bulk job fails repeatedly or shows a high failure rate.",
    "You need to send an exception or custom segment that the UI doesn’t support.",
  ],
  relatedGuides: [
    { label: "Export users to CSV", guideId: "export-users-csv" },
    { label: "Submitting a bug report", guideId: "bug-reporting" },
  ],
  relatedAdminPaths: [
    { label: "Email & Comms", path: "/email-comms" },
    { label: "Email & Comms activity", path: "/email-comms/activity" },
    { label: "Users", path: "/users" },
  ],
};
